import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import Tooltip from "@mui/material/Tooltip";
import { AI_PLAN_TOOLTIP_SLOT_PROPS } from "../TruncatedTooltipCell";
import type { EnhancedTaggingRuleWizardStep } from "./types";

type StepMeta = {
  key: EnhancedTaggingRuleWizardStep;
  title: string;
  subtitle: string;
  iconBg: string;
  iconColor: string;
  Icon: typeof AccessTimeOutlinedIcon;
};

const CONNECTOR_HEIGHT = 28;

type EnhancedTaggingRuleFlowSidebarProps = Readonly<{
  activeStep: EnhancedTaggingRuleWizardStep;
  onStepChange: (step: EnhancedTaggingRuleWizardStep) => void;
  stepSubtitles?: Partial<Record<EnhancedTaggingRuleWizardStep, string>>;
  /**
   * Steps the user cannot jump to yet (e.g. condition/then before object
   * type + sub-type are selected).
   */
  disabledSteps?:
    | ReadonlySet<EnhancedTaggingRuleWizardStep>
    | readonly EnhancedTaggingRuleWizardStep[];
  /**
   * Steps that failed validation after Submit/Next — empty prompts render
   * with red text and red border only for these.
   */
  invalidSteps?:
    | ReadonlySet<EnhancedTaggingRuleWizardStep>
    | readonly EnhancedTaggingRuleWizardStep[];
}>;

function isStepInList(
  step: EnhancedTaggingRuleWizardStep,
  list:
    | ReadonlySet<EnhancedTaggingRuleWizardStep>
    | readonly EnhancedTaggingRuleWizardStep[]
    | undefined,
): boolean {
  if (!list) return false;
  if (list instanceof Set) return list.has(step);
  return Array.isArray(list) && list.includes(step);
}

function isStepDisabled(
  step: EnhancedTaggingRuleWizardStep,
  disabledSteps:
    | ReadonlySet<EnhancedTaggingRuleWizardStep>
    | readonly EnhancedTaggingRuleWizardStep[]
    | undefined,
): boolean {
  return isStepInList(step, disabledSteps);
}

export default function EnhancedTaggingRuleFlowSidebar({
  activeStep,
  onStepChange,
  stepSubtitles,
  disabledSteps,
  invalidSteps,
}: EnhancedTaggingRuleFlowSidebarProps) {
  const steps: StepMeta[] = [
    {
      key: "when",
      title: "When: Auto-classification runs",
      subtitle: stepSubtitles?.when ?? "",
      iconBg: "#E8F8EF",
      iconColor: "#22C55E",
      Icon: AccessTimeOutlinedIcon,
    },
    {
      key: "object",
      title: "Set Object and Condition(s)",
      subtitle:
        stepSubtitles?.object ??
        "Please select assessment name, reference items and condition(s)",
      iconBg: "#FFF7E6",
      iconColor: "#FAAD14",
      Icon: TuneOutlinedIcon,
    },
    {
      key: "then",
      title: "Then: Apply tag",
      subtitle: stepSubtitles?.then ?? "Please add an enhanced tag.",
      iconBg: "#EEF3FF",
      iconColor: "#295EF1",
      Icon: BoltOutlinedIcon,
    },
  ];

  return (
    <Box
      sx={{
        flex: "1 1 50%",
        width: "50%",
        minWidth: 0,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        alignSelf: "stretch",
        pt: 0,
        pb: 0,
        px: { xs: 2, md: 4, lg: 6 },
        bgcolor: "transparent",
        overflow: "auto",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 560,
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
        }}
      >
        {steps.map((step, index) => {
          const isSelectable = !isStepDisabled(step.key, disabledSteps);
          const isActive = isSelectable && step.key === activeStep;
          const Icon = step.Icon;
          const hasSubtitle = Boolean(step.subtitle);
          const showError = isStepInList(step.key, invalidSteps);
          const showConnectorBelow = index < steps.length - 1;

          return (
            <Box key={step.key} sx={{ width: "100%" }}>
              <Box
                component={isSelectable ? "button" : "div"}
                type={isSelectable ? "button" : undefined}
                aria-pressed={isSelectable ? isActive : undefined}
                aria-disabled={!isSelectable ? true : undefined}
                onClick={
                  isSelectable ? () => onStepChange(step.key) : undefined
                }
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  textAlign: "left",
                  border: showError
                    ? "2px solid #CF1322"
                    : isActive
                      ? "2px solid #295EF1"
                      : "2px solid transparent",
                  borderRadius: "12px",
                  bgcolor: "#fff",
                  boxShadow: isActive
                    ? "0 1px 3px rgba(0, 0, 0, 0.06)"
                    : "none",
                  p: 0,
                  cursor: isSelectable ? "pointer" : "not-allowed",
                  font: "inherit",
                  color: "inherit",
                  overflow: "hidden",
                  WebkitAppearance: "none",
                  appearance: "none",
                  opacity: isSelectable ? 1 : 0.55,
                  ...(isSelectable
                    ? {
                        "&:hover": {
                          borderColor: showError
                            ? "#CF1322"
                            : isActive
                              ? "#295EF1"
                              : "transparent",
                        },
                        "&:focus-visible": {
                          outline: showError
                            ? "2px solid #CF1322"
                            : "2px solid #295EF1",
                          outlineOffset: 2,
                        },
                      }
                    : null),
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 2.5,
                    py: 2.25,
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: "10px",
                      bgcolor: step.iconBg,
                      color: step.iconColor,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon sx={{ fontSize: 24 }} />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: 600,
                      color: isSelectable ? "#262626" : "#8c8c8c",
                      lineHeight: 1.4,
                    }}
                  >
                    {step.title}
                  </Typography>
                </Box>

                {hasSubtitle ? (
                  <>
                    <Box
                      sx={{ height: 1, bgcolor: "#F0F0F0", width: "100%" }}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        px: 2.5,
                        py: 1.75,
                      }}
                    >
                      <Tooltip
                        title={showError ? "" : step.subtitle}
                        arrow
                        placement="top"
                        disableHoverListener={showError}
                        slotProps={AI_PLAN_TOOLTIP_SLOT_PROPS}
                      >
                        <Typography
                          sx={{
                            fontSize: "13px",
                            color: showError ? "#CF1322" : "#8c8c8c",
                            lineHeight: 1.4,
                            minWidth: 0,
                            flex: 1,
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {step.subtitle}
                        </Typography>
                      </Tooltip>
                      {isSelectable ? (
                        <ChevronRightIcon
                          sx={{ fontSize: 18, color: "#BFBFBF", flexShrink: 0 }}
                        />
                      ) : null}
                    </Box>
                  </>
                ) : null}
              </Box>

              {showConnectorBelow ? (
                <Box
                  aria-hidden
                  sx={{
                    width: 2,
                    height: CONNECTOR_HEIGHT,
                    bgcolor: "#E5E7EB",
                    mx: "auto",
                    flexShrink: 0,
                  }}
                />
              ) : null}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
